import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, Button } from '../components';
import {
  PROFESSIONAL_TYPE_LABELS,
  PROFESSIONAL_CATEGORIES,
  type ProfessionalCategoryId,
} from '../types/professionalRates';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import { useAuthStore } from '../store/authStore';

function eur(n: number | null | undefined) {
  if (n === null || n === undefined || Number.isNaN(n)) return '-';
  return `€${Number(n).toFixed(2)}`;
}


const REPORT_FREQUENCIES = [
  { value: 'every_conversation', labelKey: 'agreement.freqEvery' },
  { value: 'start_end_only', labelKey: 'agreement.freqStartEnd' },
  { value: 'none', labelKey: 'agreement.freqNone' },
  { value: 'custom', labelKey: 'agreement.freqCustom' },
] as const;

export const EmployerConnectionAgreementPage: React.FC = () => {
  const { t } = useTranslation('employer');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { connectionId } = useParams<{ connectionId: string }>();
  const { user } = useAuthStore();

  const { data: employerProfile } = useQuery({
    queryKey: ['employer-profile'],
    queryFn: async () => {
      const res = await api.get('/employer/profile');
      return res.data.data;
    },
    enabled: true,
  });

  // Get connection details to get professional_id
  const { data: connection } = useQuery({
    queryKey: ['employer-connection', connectionId],
    queryFn: async () => {
      if (!connectionId) return null;
      const res = await api.get(`/employer/connections`);
      const connections = res.data.data || [];
      return connections.find((c: any) => c.id === parseInt(connectionId));
    },
    enabled: !!connectionId,
  });

  const { data: agreementData, isLoading } = useQuery({
    queryKey: ['employer-connection-agreement', connectionId],
    queryFn: async () => {
      if (!connectionId) return null;
      const res = await api.get(`/employer/connections/${connectionId}/agreements`);
      return {
        agreement: res.data.data,
        vacancy: res.data.vacancy
      };
    },
    enabled: !!connectionId,
  });

  const agreement = agreementData?.agreement;
  const vacancy = agreementData?.vacancy;

  // Get professional rate info
  const { data: professionalRateInfo } = useQuery({
    queryKey: ['professional-rate-info', connection?.professional_id],
    queryFn: async () => {
      if (!connection?.professional_id) return null;
      const res = await api.get(`/professional/${connection.professional_id}/rate-info`);
      return res.data.data;
    },
    enabled: !!connection?.professional_id,
  });

  const employerFullAddress = useMemo(() => {
    const parts = [employerProfile?.adres, employerProfile?.postcode, employerProfile?.plaats].filter(Boolean);
    return parts.join(', ');
  }, [employerProfile]);

  const [createForm, setCreateForm] = useState({
    professional_function: '',
    employer_description: '',
    employer_location: '',
    discount_requested: false,
    discount_request_reason: '',
    max_conversations_per_traject: 3,
    request_approval_for_more: false,
    conversation_report_frequency: 'every_conversation' as 'every_conversation' | 'start_end_only' | 'none' | 'custom',
    custom_report_frequency: '',
    additional_agreements: '',
    // New fields from vacancy
    availability: 'per_direct' as 'per_direct' | 'datum',
    availability_date: '',
    work_preference: 'beide' as 'alleen_fysiek' | 'alleen_remote' | 'beide',
    expected_hours_per_month: '',
    work_area: '',
  });

  const [discountRequested, setDiscountRequested] = useState(false);
  const [discountReason, setDiscountReason] = useState('');

  // Sync local form from agreement
  React.useEffect(() => {
    if (agreement) {
      setDiscountRequested(agreement.discount_requested === true);
      setDiscountReason(agreement.discount_request_reason || '');
    }
  }, [agreement]);

  // Prefill form from vacancy when creating new agreement
  React.useEffect(() => {
    if (!agreement && vacancy) {
      // Map vacancy fields to form fields
      const vacancyReportFrequency = vacancy.report_frequency === 'ieder_gesprek' ? 'every_conversation' :
        vacancy.report_frequency === 'begin_eind' ? 'start_end_only' :
        vacancy.report_frequency === 'geen' ? 'none' :
        vacancy.report_frequency === 'aangepast' ? 'custom' : 'every_conversation';

      const workPreferenceMap: Record<string, 'alleen_fysiek' | 'alleen_remote' | 'beide'> = {
        'physical': 'alleen_fysiek',
        'remote': 'alleen_remote',
        'hybrid': 'beide',
        'remote_only': 'alleen_remote',
        'face_to_face': 'alleen_fysiek',
        'no_preference': 'beide'
      };

      const availability = vacancy.availability_needed === 'specific_date' ? 'datum' : 'per_direct';
      const workPreference = workPreferenceMap[vacancy.work_preference] || 'beide';
      const workArea = Array.isArray(vacancy.active_regions) ? vacancy.active_regions.join(', ') : (vacancy.active_regions || '');
      const expectedHours = vacancy.hours_per_month_min || vacancy.hours_per_month_max || vacancy.expected_hours_per_month || '';

      setCreateForm((prev) => ({
        professional_function: vacancy.professional_type || prev.professional_function,
        employer_description: vacancy.beschrijving || prev.employer_description,
        employer_location: vacancy.company_location || employerFullAddress || prev.employer_location,
        discount_requested: vacancy.discount_requested || prev.discount_requested,
        discount_request_reason: vacancy.discount_request_reason || prev.discount_request_reason,
        max_conversations_per_traject: vacancy.max_appointments_per_traject || prev.max_conversations_per_traject,
        conversation_report_frequency: vacancyReportFrequency,
        custom_report_frequency: vacancy.report_frequency === 'aangepast' ? (vacancy.custom_report_frequency || '') : prev.custom_report_frequency,
        additional_agreements: vacancy.additional_agreements || prev.additional_agreements,
        availability,
        availability_date: vacancy.availability_date || vacancy.available_from_date || '',
        work_preference: workPreference,
        expected_hours_per_month: expectedHours.toString(),
        work_area: workArea,
        request_approval_for_more: prev.request_approval_for_more,
      }));
    } else if (!agreement && employerFullAddress) {
      // Fallback: prefill location from employer profile (alleen als nog leeg)
      setCreateForm((prev) => (prev.employer_location ? prev : { ...prev, employer_location: employerFullAddress }));
    }
  }, [agreement, vacancy, employerFullAddress]);

  const effectiveHourlyRate = useMemo(() => {
    if (!agreement) return null;
    return agreement.effective_hourly_rate ?? agreement.hourly_rate ?? null;
  }, [agreement]);

  const createAgreementMutation = useMutation({
    mutationFn: async () => {
      if (!connectionId) throw new Error(t('agreement.missingConnectionId'));
      if (!createForm.professional_function) {
        throw new Error(t('agreement.selectFunctionFirst'));
      }
      if (createForm.discount_requested && !createForm.discount_request_reason.trim()) {
        throw new Error(t('agreement.discountReasonRequired'));
      }
      return await api.post(`/employer/connections/${connectionId}/agreements`, {
        professional_function: createForm.professional_function,
        employer_description: createForm.employer_description || null,
        employer_location: createForm.employer_location || null,
        discount_requested: createForm.discount_requested,
        discount_request_reason: createForm.discount_requested ? createForm.discount_request_reason : null,
        max_conversations_per_traject: createForm.max_conversations_per_traject,
        conversation_report_frequency: createForm.conversation_report_frequency,
        custom_report_frequency: createForm.conversation_report_frequency === 'custom' ? createForm.custom_report_frequency : null,
        additional_agreements: createForm.additional_agreements || null,
      });
    },
    onSuccess: () => {
      toast.success(t('agreement.createSuccess'));
      queryClient.invalidateQueries({ queryKey: ['employer-connection-agreement', connectionId] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || err?.message || t('agreement.createError'));
    }
  });

  const saveDiscountRequestMutation = useMutation({
    mutationFn: async () => {
      if (!connectionId) throw new Error(t('agreement.missingConnectionId'));
      return await api.patch(`/employer/connections/${connectionId}/agreements`, {
        discount_requested: discountRequested,
        discount_request_reason: discountRequested ? discountReason : '',
      });
    },
    onSuccess: () => {
      toast.success(t('agreement.discountSaved'));
      queryClient.invalidateQueries({ queryKey: ['employer-connection-agreement', connectionId] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || err?.message || t('agreement.discountSaveError'));
    }
  });

  const approveMutation = useMutation({
    mutationFn: async () => {
      if (!connectionId) throw new Error(t('agreement.missingConnectionId'));
      return await api.post(`/employer/connections/${connectionId}/agreements/approve`);
    },
    onSuccess: () => {
      toast.success(t('agreement.approveSuccess'));
      queryClient.invalidateQueries({ queryKey: ['employer-connection-agreement', connectionId] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || err?.message || t('agreement.approveError'));
    }
  });

  if (!connectionId) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card>
          <div className="text-center py-12 space-y-3">
            <p className="text-gray-700">{t('agreement.invalidConnection')}</p>
            <Button variant="outline" onClick={() => navigate('/employer/connections')}>
              {t('agreement.back')}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card>
          <div className="text-center py-12">
            <p className="text-gray-600">{t('agreement.loading')}</p>
          </div>
        </Card>
      </div>
    );
  }

  if (!agreement) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card>
          <div className="p-6 space-y-6">
            <div className="space-y-1">
              <h1 className="text-2xl font-bold text-gray-900">{t('agreement.createTitle')}</h1>
              <p className="text-gray-600">
                {t('agreement.createDesc')}
              </p>
            </div>

            {/* Basisgegevens - Read-only from employer profile */}
            <div className="p-4 bg-gray-50 rounded-lg space-y-3">
              <div className="text-sm font-medium text-gray-900 mb-3">{t('agreement.basicInfo')}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">{t('agreement.employer')}</label>
                  <div className="text-sm font-medium text-gray-900">
                    {employerProfile?.bedrijfsnaam || '-'}
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">{t('agreement.contactPerson')}</label>
                  <div className="text-sm font-medium text-gray-900">
                    {employerProfile?.contactpersoon || `${employerProfile?.first_name || ''} ${employerProfile?.last_name || ''}`.trim() || '-'}
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">{t('agreement.mobile')}</label>
                  <div className="text-sm font-medium text-gray-900">
                    {user?.phone || '-'}
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">{t('agreement.email')}</label>
                  <div className="text-sm font-medium text-gray-900">
                    {user?.email || '-'}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('agreement.professionalType')}</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={createForm.professional_function}
                onChange={(e) => setCreateForm((p) => ({ ...p, professional_function: e.target.value }))}
              >
                <option value="">{t('agreement.selectFunction')}</option>
                {(Object.entries(PROFESSIONAL_CATEGORIES) as [ProfessionalCategoryId, typeof PROFESSIONAL_CATEGORIES[ProfessionalCategoryId]][]).map(([catId, cat]) => (
                  <optgroup key={catId} label={cat.label}>
                    {cat.types.map(type => (
                      <option key={type} value={type}>{PROFESSIONAL_TYPE_LABELS[type]}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('agreement.description')}</label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                value={createForm.employer_description}
                onChange={(e) => setCreateForm((p) => ({ ...p, employer_description: e.target.value }))}
                placeholder={t('agreement.descriptionPlaceholder')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('agreement.collaborationDesc')}</label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                value={createForm.employer_description}
                onChange={(e) => setCreateForm((p) => ({ ...p, employer_description: e.target.value }))}
                placeholder={t('agreement.descriptionPlaceholder')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('agreement.availability')}</label>
              <div className="flex gap-4 mb-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="per_direct"
                    checked={createForm.availability === 'per_direct'}
                    onChange={(e) => setCreateForm((p) => ({ ...p, availability: e.target.value as 'per_direct' | 'datum' }))}
                    className="mr-2"
                  />
                  {t('agreement.availabilityImmediate')}
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="datum"
                    checked={createForm.availability === 'datum'}
                    onChange={(e) => setCreateForm((p) => ({ ...p, availability: e.target.value as 'per_direct' | 'datum' }))}
                    className="mr-2"
                  />
                  {t('agreement.availabilityDate')}
                </label>
              </div>
              {createForm.availability === 'datum' && (
                <input
                  type="date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={createForm.availability_date}
                  onChange={(e) => setCreateForm((p) => ({ ...p, availability_date: e.target.value }))}
                  min={new Date().toISOString().split('T')[0]}
                />
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('agreement.companyLocation')}</label>
              <input
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={createForm.employer_location}
                onChange={(e) => setCreateForm((p) => ({ ...p, employer_location: e.target.value }))}
                placeholder={t('agreement.companyLocationPlaceholder')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('agreement.workArea')}</label>
              <input
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={createForm.work_area}
                onChange={(e) => setCreateForm((p) => ({ ...p, work_area: e.target.value }))}
                placeholder={t('agreement.workAreaPlaceholder')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('agreement.workPreference')}</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={createForm.work_preference}
                onChange={(e) => setCreateForm((p) => ({ ...p, work_preference: e.target.value as 'alleen_fysiek' | 'alleen_remote' | 'beide' }))}
              >
                <option value="alleen_fysiek">{t('agreement.workPrefPhysical')}</option>
                <option value="alleen_remote">{t('agreement.workPrefRemote')}</option>
                <option value="beide">{t('agreement.workPrefBoth')}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('agreement.expectedHours')}</label>
              <input
                type="number"
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={createForm.expected_hours_per_month}
                onChange={(e) => setCreateForm((p) => ({ ...p, expected_hours_per_month: e.target.value }))}
                placeholder={t('agreement.expectedHoursPlaceholder')}
              />
            </div>

            {/* Uurtarief display */}
            {professionalRateInfo && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="text-sm font-medium text-gray-900 mb-2">{t('agreement.professionalRate')}</div>
                <div className="text-lg font-semibold text-gray-900 mb-1">
                  {eur(professionalRateInfo.hourly_rate)}
                </div>
                <p className="text-xs text-gray-600">
                  {t('agreement.rateNote')}
                </p>
              </div>
            )}

            <div className="p-4 border border-gray-200 rounded-lg space-y-3">
              <div className="text-sm font-medium text-gray-900">{t('agreement.requestDiscount')}</div>
              <label className="flex items-center text-sm">
                <input
                  type="checkbox"
                  checked={createForm.discount_requested}
                  onChange={(e) => setCreateForm((p) => ({ ...p, discount_requested: e.target.checked }))}
                  className="mr-2"
                />
                {t('agreement.requestDiscountLabel')}
              </label>
              {createForm.discount_requested && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('agreement.discountReason')}</label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    value={createForm.discount_request_reason}
                    onChange={(e) => setCreateForm((p) => ({ ...p, discount_request_reason: e.target.value }))}
                  />
                </div>
              )}
              <p className="text-xs text-gray-500">
                {t('agreement.discountNote')}
              </p>
            </div>

            <div className="p-4 border border-gray-200 rounded-lg space-y-4">
              <div className="text-sm font-medium text-gray-900">{t('agreement.appointments')}</div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('agreement.maxAppointments')}</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={createForm.max_conversations_per_traject}
                  onChange={(e) => setCreateForm((p) => ({ ...p, max_conversations_per_traject: parseInt(e.target.value) }))}
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="flex items-center text-sm">
                  <input
                    type="checkbox"
                    checked={createForm.request_approval_for_more}
                    onChange={(e) => setCreateForm((p) => ({ ...p, request_approval_for_more: e.target.checked }))}
                    className="mr-2"
                  />
                  {t('agreement.requestApprovalForMore')}
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('agreement.reportFrequency')}</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={createForm.conversation_report_frequency}
                  onChange={(e) => setCreateForm((p) => ({ ...p, conversation_report_frequency: e.target.value as any }))}
                >
                  {REPORT_FREQUENCIES.map((f) => (
                    <option key={f.value} value={f.value}>{t(f.labelKey)}</option>
                  ))}
                </select>
              </div>

              {createForm.conversation_report_frequency === 'custom' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('agreement.customFrequency')}</label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={createForm.custom_report_frequency}
                    onChange={(e) => setCreateForm((p) => ({ ...p, custom_report_frequency: e.target.value }))}
                    placeholder={t('agreement.customFrequencyPlaceholder')}
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('agreement.additionalAgreements')}</label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  value={createForm.additional_agreements}
                  onChange={(e) => setCreateForm((p) => ({ ...p, additional_agreements: e.target.value }))}
                  placeholder={t('agreement.additionalAgreementsPlaceholder')}
                />
              </div>
            </div>

            <div className="flex gap-3 pt-2 border-t">
              <Button variant="outline" onClick={() => navigate('/employer/connections')}>
                {t('agreement.back')}
              </Button>
              <Button
                onClick={() => createAgreementMutation.mutate()}
                loading={createAgreementMutation.isPending}
                disabled={createAgreementMutation.isPending}
              >
                {t('agreement.create')}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  const approvedByEmployer = agreement.approved_by_employer === true;
  const approvedByProfessional = agreement.approved_by_professional === true;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <Button variant="outline" onClick={() => navigate('/employer/connections')}>
          {t('agreement.back')}
        </Button>
      </div>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('agreement.title')}</h1>
        <p className="mt-1 text-gray-600">
          {t('agreement.subtitle')}
        </p>
      </div>

      <Card>
        <div className="p-6 space-y-6">

          {/* ── Tariefoverzicht ── */}
          <div>
            <h2 className="text-base font-semibold text-gray-900 mb-3">{t('agreement.hourlyRate')}</h2>

            {/* Advisory rate reference */}
            {agreement.advisory_rate != null && (
              <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                <div className="text-blue-800 font-medium">
                  {t('agreement.advisoryRate')}: <span className="font-bold">{eur(agreement.advisory_rate)}</span>
                </div>
                <div className="text-blue-700 text-xs mt-0.5">
                  {t('agreement.advisoryRateNote')}
                </div>
              </div>
            )}

            {/* Professional's rate choice */}
            {agreement.rate_choice && (
              <div className={`mb-3 p-3 rounded-lg border text-sm ${
                agreement.rate_choice === 'advisory'
                  ? 'bg-green-50 border-green-200'
                  : agreement.rate_choice === 'higher'
                  ? 'bg-amber-50 border-amber-200'
                  : 'bg-purple-50 border-purple-200'
              }`}>
                <div className="font-medium text-gray-900 mb-0.5">
                  {t('agreement.professionalChoice')}:{' '}
                  {agreement.rate_choice === 'advisory' && t('agreement.rateChoiceAdvisory')}
                  {agreement.rate_choice === 'higher' && t('agreement.rateChoiceHigher')}
                  {agreement.rate_choice === 'lower' && t('agreement.rateChoiceLower')}
                </div>
                {agreement.rate_deviation_reason && (
                  <div className="text-gray-700 mt-1 whitespace-pre-wrap">
                    <span className="text-xs font-medium text-gray-500">{t('agreement.reasoning')}: </span>
                    {agreement.rate_deviation_reason}
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div className="p-3 bg-gray-50 rounded border">
                <div className="text-gray-500">{t('agreement.rateExclFee')}</div>
                <div className="text-gray-900 font-semibold text-lg">{eur(agreement.hourly_rate)}</div>
              </div>
              {Number(agreement.discount_percent) > 0 && (
                <>
                  <div className="p-3 bg-gray-50 rounded border">
                    <div className="text-gray-500">{t('agreement.discountPercent')}</div>
                    <div className="text-gray-900 font-semibold">{Number(agreement.discount_percent).toFixed(2)}%</div>
                  </div>
                  <div className="p-3 bg-gray-50 rounded border">
                    <div className="text-gray-500">{t('agreement.rateAfterDiscount')}</div>
                    <div className="text-gray-900 font-semibold">{eur(effectiveHourlyRate)}</div>
                  </div>
                </>
              )}
              <div className="p-3 bg-gray-50 rounded border">
                <div className="text-gray-500">{t('agreement.function')}</div>
                <div className="text-gray-900 font-semibold">{agreement.professional_function?.replace(/_/g, ' ')}</div>
              </div>
            </div>
          </div>

          {/* ── Tariefafspraken ── */}
          {(agreement.assignment_start_date || agreement.inflation_correction_percent != null || agreement.rate_review_duration_years != null) && (
            <div>
              <h2 className="text-base font-semibold text-gray-900 mb-3">{t('agreement.rateAgreements')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                {agreement.assignment_start_date && (
                  <div className="p-3 bg-gray-50 rounded border">
                    <div className="text-gray-500">{t('agreement.startDate')}</div>
                    <div className="text-gray-900 font-semibold">
                      {new Date(agreement.assignment_start_date).toLocaleDateString('nl-NL')}
                    </div>
                  </div>
                )}
                {agreement.inflation_correction_percent != null && (
                  <div className="p-3 bg-gray-50 rounded border">
                    <div className="text-gray-500">{t('agreement.inflationCorrection')}</div>
                    <div className="text-gray-900 font-semibold">{Number(agreement.inflation_correction_percent).toFixed(1)}%</div>
                  </div>
                )}
                {agreement.rate_review_duration_years != null && (
                  <div className="p-3 bg-gray-50 rounded border">
                    <div className="text-gray-500">{t('agreement.rateDuration')}</div>
                    <div className="text-gray-900 font-semibold">{agreement.rate_review_duration_years} {t('agreement.years')}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Korting aanvragen ── */}
          <div className="p-4 border border-gray-200 rounded-lg space-y-3">
            <div className="text-sm font-medium text-gray-900">{t('agreement.requestDiscountFromPro')}</div>
            <label className="flex items-center text-sm">
              <input
                type="checkbox"
                checked={discountRequested}
                onChange={(e) => setDiscountRequested(e.target.checked)}
                className="mr-2"
              />
              {t('agreement.requestDiscountLabel')}
            </label>
            {discountRequested && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('agreement.discountReason')}</label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  value={discountReason}
                  onChange={(e) => setDiscountReason(e.target.value)}
                  placeholder={t('agreement.discountReasonPlaceholder')}
                />
              </div>
            )}
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  if (discountRequested && !discountReason.trim()) {
                    toast.error(t('agreement.discountReasonRequired'));
                    return;
                  }
                  saveDiscountRequestMutation.mutate();
                }}
                loading={saveDiscountRequestMutation.isPending}
              >
                {t('agreement.saveDiscount')}
              </Button>
            </div>
            <p className="text-xs text-gray-500">
              {t('agreement.discountNote')}
            </p>
          </div>

          {/* ── Status acceptatie ── */}
          <div className="p-4 border border-gray-200 rounded-lg text-sm space-y-2">
            <div className="font-medium text-gray-900">{t('agreement.acceptanceStatus')}</div>
            <div className="flex items-center gap-2 text-gray-700">
              <span className={`inline-block w-2.5 h-2.5 rounded-full ${approvedByProfessional ? 'bg-green-500' : 'bg-gray-300'}`} />
              {t('agreement.professional')}: <span className="font-medium">{approvedByProfessional ? t('agreement.accepted') : t('agreement.waiting')}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <span className={`inline-block w-2.5 h-2.5 rounded-full ${approvedByEmployer ? 'bg-green-500' : 'bg-gray-300'}`} />
              {t('agreement.employer')}: <span className="font-medium">{approvedByEmployer ? t('agreement.accepted') : t('agreement.waiting')}</span>
            </div>
          </div>

          <div className="flex gap-3 pt-2 border-t">
            <Button variant="outline" onClick={() => navigate('/employer/connections')}>
              {t('agreement.close')}
            </Button>
            <Button
              onClick={() => approveMutation.mutate()}
              loading={approveMutation.isPending}
              disabled={approvedByEmployer}
            >
              {approvedByEmployer ? t('agreement.alreadyAccepted') : t('agreement.acceptAgreements')}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
